import { Box, Typography, SxProps, Theme } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import { PinTagButton } from "../../../atoms/pintag-button/pintag-button";
import { CustomTextBox } from "../../../atoms";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks/reduxHooks";
import {
  getAllColorPinTagsAction,
  getAllServicePinTagsAction,
} from "../../../../redux/actions/pintagAction";
import { getStoreClosureDates } from "../../../../redux/actions/storeClosureDateActions";
import { refreshStoreConfig } from "../../../../redux/actions/storeActions";
import { IColorPintag } from "../../../../typings/interfaces/pos/pintag";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import { cloneDeep } from "lodash";
import { addSession } from "../../../../redux/actions/cartAction";
import { pxToRem, isWorkingDay } from "../../../../util";
import { useSpecialDate } from "../../../../hooks/useSpecialDate";
import { useRegularDate } from "../../../../hooks/useRegularDate";
import { ShadcnDatePicker } from "../../../atoms/custom-date-time-picker/ShadcnDatePicker";
import { HolidayDatesDialog } from "./holiday-dates-dialog";

export interface IPinTagsSectionProps {
  sx?: SxProps<Theme>;
  itemsCount: number;
  serviceTypeId: number;
  sessionId: string;
  isPintagModalSection?: boolean;
  setIsCompleted?: (isCompleted: boolean) => void;
  resetTagsTrigger?: number;
  resetFlipIndex?: number | null;
  initialColor?: string;
  initialDate?: string;
  mode?: string;
  screen?: string;
  onCalendarOpenChange?: (isOpen: boolean) => void;
  setResetFlipIndex?: (index: number | null) => void;
  onChange?: (
    color: string | null,
    tags: string[],
    textBoxCount: number,
    colorId?: number,
    alias?: string,
    day?: string,
    date?: string | null,
    isSpecial?: boolean
  ) => void;
  onTypingStart?: () => void;
  // Called when input changed but nothing was sent for validation
  onValidationIdle?: () => void;
  initialTags?: string[];
}

type PinTagValidationResult = {
  ok: boolean;
  dupInOther: string[];
  dupInCurrent: string[];
  dupIndices: number[];
};

// Max automatic re-fetch attempts for the color/service pintag lists before we
// stop retrying and just let the error stand (see the retry-loop effects below).
const MAX_PINTAG_FETCH_RETRIES = 3;

type DateMode = "regular" | "special" | "custom";

// The holiday explanation is shown once per ticket and situation, not every
// time the modal or drawer for that ticket opens again
const holidayNoticeShown = new Set<string>();

const getInitialDateMode = (
  initialDate: string | undefined,
  regularDate: Dayjs,
  specialDate: Dayjs
): DateMode => {
  if (!initialDate) return "special";
  const initial = dayjs(initialDate);

  if (initial.isSame(regularDate, "day")) return "regular";

  if (initial.isSame(specialDate, "day")) return "special";

  return "custom";
};

export const PinTagsSection: FC<IPinTagsSectionProps> = ({
  sx = {},
  itemsCount,
  serviceTypeId,
  sessionId,
  isPintagModalSection = false,
  setIsCompleted,
  onChange,
  resetFlipIndex,
  setResetFlipIndex,
  initialColor,
  initialDate,
  screen,
  onTypingStart,
  onValidationIdle,
  onCalendarOpenChange,
  mode,
  initialTags,
}) => {
  const {
    date: regularDate,
    isReady: regularDateReady,
    holidays: regularHolidays,
    isAdminDate: regularIsAdminDate,
  } = useRegularDate();
  const {
    date: specialDate,
    isReady: specialDateReady,
    holidays: specialHolidays,
    isAdminDate: specialIsAdminDate,
  } = useSpecialDate();
  // True once store config has settled (loaded or failed) - both hooks read
  // the same store config, so in practice these flip together, but combining
  // them defensively avoids acting on a half-ready state.
  const isDateConfigReady = regularDateReady && specialDateReady;

  // Holidays the Special / Regular dates skip (3.f): explain them once
  const skippedHolidays = useMemo(() => {
    const byDate = new Map(
      [...specialHolidays, ...regularHolidays].map((h) => [h.date, h])
    );
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [specialHolidays, regularHolidays]);
  const holidayNoticeKey = skippedHolidays.length
    ? [
        sessionId,
        skippedHolidays.map((h) => h.date).join(","),
        specialDate.format("YYYY-MM-DD"),
        regularDate.format("YYYY-MM-DD"),
      ].join("|")
    : null;
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  useEffect(() => {
    if (!isDateConfigReady || !holidayNoticeKey) return;
    if (holidayNoticeShown.has(holidayNoticeKey)) return;
    holidayNoticeShown.add(holidayNoticeKey);
    setHolidayDialogOpen(true);
  }, [isDateConfigReady, holidayNoticeKey]);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [sortedPinTags, setSortedPinTags] = useState<IColorPintag[]>([]);
  // `null` here means "not yet determined" - deliberately not defaulting to
  // "special"/"custom" while store config is still loading, so the UI shows
  // no active tab instead of guessing and possibly snapping to a different
  // tab once the real config-derived dates arrive (see the settle effect
  // below, which resolves this to a real mode once config is ready).
  const [dateMode, setDateMode] = useState<DateMode | null>(() => {
    if (!isDateConfigReady) return null;
    return getInitialDateMode(initialDate, regularDate, specialDate);
  });
  const regularTabRef = useRef<HTMLDivElement>(null);
  const [customTabPosition, setCustomTabPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const customTabRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const tagInputRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const inputRefs = useRef<Map<number, HTMLInputElement | null>>(new Map());
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [textBoxCount, setTextBoxCount] = useState<number>(1);
  const [shakingIndices, setShakingIndices] = useState<number[]>([]);
  const [selectedOption, setSelectedOption] = useState("today");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const colorPintagRetryCountRef = useRef(0);
  const servicePintagRetryCountRef = useRef(0);
  // Guards the initialColor/initialTags seeding effect below so it only seeds
  // once per session instead of every time `initialTags` changes identity - see
  // the comment on that effect for why a naive `[initialColor, initialTags,
  // sortedPinTags]` dependency array causes an infinite validate loop. Keyed off
  // `sessionId` (reset below whenever it actually changes) rather than relying
  // solely on remount, since not every consumer of this component keys it by
  // session (PinTagDrawer does; AssignPinTagModal does not).
  const initialSeedDoneRef = useRef(false);
  const seededSessionIdRef = useRef<string | undefined>(undefined);
  // Tags typed on each date tab, so switching tabs and back keeps them (3.t)
  const tagsByTabRef = useRef<Partial<Record<DateMode, string[]>>>({});
  if (seededSessionIdRef.current !== sessionId) {
    seededSessionIdRef.current = sessionId;
    initialSeedDoneRef.current = false;
    tagsByTabRef.current = {};
  }

  const {
    getColorPintagsError,
    getServicePintagsError,
    getColorPintags,
    getServicePintags,
  } = useAppSelector((state) => state.pintag);
  // const [sessionTagsLength, setSessionTagsLength] = useState<number>(0);

  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  // With an explicit `initialDate` it's authoritative from the start. With no
  // `initialDate`, don't settle on `specialDate` until store config is known
  // to be ready - otherwise this freezes on whatever guess the hook returned
  // before config loaded (the bug this fixes). The settle effect below picks
  // this up once `isDateConfigReady` flips true.
  const [dueDate, setDueDate] = useState<Dayjs | null>(() => {
    if (initialDate) return dayjs(initialDate);
    return isDateConfigReady ? specialDate : null;
  });
  const [selectedColor, setSelectedColor] = useState<string | null>(
    initialColor ?? null
  );
  const storeConfig = useAppSelector(
    (state) => state.store.getStoreConfigResponse
  );
  const { closureDates } = useAppSelector((state) => state.storeClosureDate);
  const { user } = useAppSelector((state) => state.auth);
  const dateInputRef = useRef<HTMLInputElement | null>(null);

  const { cart } = useAppSelector((state) => state.cart);
  const [serviceTagCount, setServiceTagCount] = useState<number>(1);
  const [customDate, setCustomDate] = useState<Dayjs | null>(
    initialDate ? dayjs(initialDate) : null
  );

  // Settle `dueDate`/`dateMode` once store config resolves. This only needs
  // to run the first time config becomes ready after mount - guarded by the
  // ref below so it never overwrites a date the user picked afterwards (e.g.
  // switching tabs or picking a custom date once the modal is already up).
  const configSettledRef = useRef(false);
  useEffect(() => {
    if (!isDateConfigReady || configSettledRef.current) return;
    configSettledRef.current = true;

    if (initialDate) {
      // initialDate stays authoritative - only re-evaluate which tab it
      // belongs to now that regularDate/specialDate reflect real config.
      setDateMode(getInitialDateMode(initialDate, regularDate, specialDate));
    } else {
      setDueDate(specialDate);
      setDateMode("special");
    }
  }, [isDateConfigReady, initialDate, regularDate, specialDate]);

  useEffect(() => {
    const updatePosition = () => {
      if (regularTabRef.current) {
        const rect = regularTabRef.current.getBoundingClientRect();
        setCustomTabPosition({
          top: rect.bottom + 8,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    if (calendarOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      return () => window.removeEventListener("resize", updatePosition);
    }
  }, [calendarOpen]);

  useEffect(() => {
    if (typeof resetFlipIndex === "number") {
      const newTags = [...selectedTags];
      newTags[resetFlipIndex] = "";
      setSelectedTags(newTags);

      triggerShake(resetFlipIndex);

      setResetFlipIndex?.(null);
    }
  }, [resetFlipIndex]);

  const handleDateModeChange = (mode: "regular" | "special" | "custom") => {
    // Re-clicking the active Special/Regular tab used to clear the colour
    // without re-picking it (same due date, so the colour effect never re-ran),
    // leaving Apply disabled
    if (mode === dateMode && mode !== "custom") return;

    // Clicking Custom again opens the calendar to change its date; the tags
    // stay as they are
    if (mode === "custom" && dateMode === "custom") {
      setCalendarOpen(true);
      return;
    }

    // Keep this tab's tags and bring back the ones typed on the other tab
    // (empty if none yet)
    if (dateMode) tagsByTabRef.current[dateMode] = selectedTags;
    const tabTags = tagsByTabRef.current[mode] ?? Array(9).fill("");
    setDateMode(mode);
    setSelectedTags(tabTags);
    setIsCompleted?.(false);
    // The colour is picked again from the tab's date by the effect below
    setSelectedColor(null);

    if (mode === "regular") {
      setDueDate(regularDate);
      setCalendarOpen(false);
    } else if (mode === "special") {
      setDueDate(specialDate);
      setCalendarOpen(false);
    } else if (mode === "custom") {
      // Back to a Custom date picked before (customDate starts as the
      // ticket's saved date, which may be the Special or Regular date)
      const pickedCustom =
        customDate &&
        !customDate.isSame(specialDate, "day") &&
        !customDate.isSame(regularDate, "day");
      if (pickedCustom) {
        setDueDate(customDate);
        setCalendarOpen(false);
      } else {
        setDueDate(null);
        setSelectedDate(undefined);
        setCalendarOpen(true);
      }
    }

    // Until the tab's tags are checked again, nothing is ready to apply
    if (isPintagModalSection && onChange) {
      onChange(null, tabTags, textBoxCount, undefined, undefined, undefined, null);
    }
  };

  useEffect(() => {
    onCalendarOpenChange?.(calendarOpen);
  }, [calendarOpen]);

  const triggerShake = (index: number) => {
    setShakingIndices((prev) => [...prev, index]);
    setTimeout(() => {
      setShakingIndices((prev) => prev.filter((i) => i !== index));
    }, 500);
  };

  // The tab picked decides Special / Regular for the bill (the check-in
  // summary's Special switches are hidden); a Custom date leaves it unset
  const pickedSpecial =
    dateMode === "special" ? true : dateMode === "regular" ? false : undefined;

  const handleAssignPinTag = (color: string | null, tags: string[]) => {
    const targetTagList = tags.slice(0, textBoxCount);
    let isIncompleteTags =
      targetTagList.filter((tag) => tag.length !== 3).length > 0;

    // alert(`${color} ${targetTagList.join(", ")} ${isIncompleteTags}`);

    if (
      color === null ||
      tags.length === 0 ||
      targetTagList.includes("") ||
      isIncompleteTags ||
      !dueDate ||
      dueDate.isBefore(dayjs().startOf("day"))
    ) {
      setIsCompleted && setIsCompleted(false);
    } else {
      if (!dueDate || dueDate.isBefore(dayjs().startOf("day"))) {
        snackbar.showSnackbar(
          "Please select a valid future due date",
          "warning"
        );
      }

      if (isPintagModalSection) {
        // Always resolve and send the FULL metadata (colorId/alias/day/date) from
        // this single call site. Previously this fired an incomplete 3-arg call
        // unconditionally, while a separate caller fired a full 7-arg call only
        // when the matched color pintag happened to have a `.day` set - since the
        // shared onChange is lodash-debounced (trailing edge keeps only the LAST
        // call's args), colors without a `.day` mapping silently lost the picked
        // due date. Resolving the full metadata here removes that race entirely.
        const matchedPinTag = sortedPinTags.find(
          (tag) => tag.alias === color || tag.color === color
        );
        onChange &&
          onChange(
            matchedPinTag?.color ?? color,
            targetTagList,
            textBoxCount,
            matchedPinTag?.id,
            matchedPinTag?.alias,
            matchedPinTag?.day ? matchedPinTag.day.toUpperCase() : undefined,
            dueDate ? dueDate.format("YYYY-MM-DD") : null,
            pickedSpecial
          );
      } else {
        let newSessionClone = cloneDeep(
          cart.find((session) => session.sessionId === sessionId)
        );
        if (newSessionClone) {
          newSessionClone.pinTag = {
            color: color,
            tags: tags,
            date: dueDate ? dueDate.format("YYYY-MM-DD") : null,
            isSpecial: pickedSpecial,
          };
          dispatch(addSession(newSessionClone));
        }
      }
    }
  };

  useEffect(() => {
    if ((getColorPintags?.body?.length ?? 0) > 0) {
      const sortedPinTags = [...(getColorPintags?.body ?? [])].sort(
        (a, b) => a.orderIndex - b.orderIndex
      );
      setSortedPinTags(sortedPinTags);
    }
  }, [getColorPintags]);
  useEffect(() => {
    if ((getServicePintags?.body?.length ?? 0) > 0) {
      // Compare as numbers: bills from the Pintag page carry serviceTypeId as
      // a string ("16"), so === never matched and every service fell back to
      // one tag per item
      const servicePintag = getServicePintags?.body.find(
        (servicePintag) => Number(servicePintag.serviceTypeId) === Number(serviceTypeId)
      );
      setServiceTagCount(servicePintag?.tagCount ?? 1);
    }
  }, [getServicePintags, serviceTypeId]);

  // Bounded retry for the color/service pintag fetches: pintagSlice resets these
  // error fields to null on `.pending` and back to a message on `.rejected`, so a
  // persistently-failing initial fetch used to retry with zero backoff/cap and
  // pegged the CPU in a tight dispatch->fail->dispatch loop. Cap retries and stop
  // looping once the limit is hit, surfacing the failure via the snackbar instead.
  //
  // IMPORTANT: do NOT reset the retry counter just because the error is falsy -
  // `dispatch(getAllColorPinTagsAction())` itself synchronously flips the error to
  // null the moment it's re-dispatched (the `.pending` case), so resetting the
  // counter on that same falsy transition would defeat the cap entirely and
  // reproduce the exact unbounded loop this is meant to fix. Once a fetch
  // genuinely succeeds, the error simply stays null/falsy forever (nothing else
  // re-dispatches these actions), so there's no need to reset the counter at all -
  // a stale non-zero count is harmless since the effect only acts while the error
  // is truthy.
  useEffect(() => {
    if (!getColorPintagsError) {
      return;
    }
    if (colorPintagRetryCountRef.current >= MAX_PINTAG_FETCH_RETRIES) {
      return;
    }
    colorPintagRetryCountRef.current += 1;
    dispatch(getAllColorPinTagsAction()).catch((error) => {
      snackbar.showSnackbar(error.message, "error");
    });
  }, [getColorPintagsError]);

  useEffect(() => {
    if (!getServicePintagsError) {
      return;
    }
    if (servicePintagRetryCountRef.current >= MAX_PINTAG_FETCH_RETRIES) {
      return;
    }
    servicePintagRetryCountRef.current += 1;
    dispatch(getAllServicePinTagsAction()).catch((error) => {
      snackbar.showSnackbar(error.message, "error");
    });
  }, [getServicePintagsError]);

  useEffect(() => {
    const session = cart.find((session) => session.sessionId === sessionId);

    // Calculate total item count as sum of (qty for each cartItem)
    const totalItemCount = session?.cartItems?.reduce(
      (acc, item) => acc + (item.qty ?? 1),
      0
    ) ?? itemsCount ?? 1;

    const selectedTagsLength = Math.ceil(totalItemCount / serviceTagCount);

    if (session) {
      // Only restore a colour the ticket already has. This also re-runs when
      // the service pintag list (tags per item) arrives, which can be after
      // the due-date effect picked the colour; clearing it then left the date
      // selected with no colour, and nothing re-picked it
      if (session.pinTag?.color) setSelectedColor(session.pinTag.color);
      const existingTags = session.pinTag?.tags ?? [];
      selectedTagsLength !== 9 &&
        setSelectedTags(
          existingTags.concat(
            Array.from({ length: 9 - selectedTagsLength }).map(() => "")
          )
        );
      setTextBoxCount(selectedTagsLength);
    } else {
      // A bill from the Pintag page isn't a cart session: apply the same
      // book rule to its item count, keeping room for tags it already has
      const existingTagCount = (initialTags ?? []).filter(Boolean).length;
      setTextBoxCount(
        Math.min(9, Math.max(1, selectedTagsLength, existingTagCount))
      );
    }
  }, [cart, serviceTagCount, itemsCount]);

  useEffect(() => {
    dispatch(getAllColorPinTagsAction());
    dispatch(getAllServicePinTagsAction());
    if (user?.storeId) {
      dispatch(getStoreClosureDates({ storeId: user.storeId, merchantId: 1 }));
      // The POS loads the store config once at login; re-read it so ready
      // date rules changed in the admin panel since then apply here
      dispatch(refreshStoreConfig({ storeId: user.storeId, merchantId: 1 }));
    }
  }, [user?.storeId]);

  // Focus the first input field when modal opens or date mode changes. Not
  // while the Custom calendar is open: the input sits below it, and focusing
  // it scrolled the drawer to the bottom (picking a date focuses it after)
  useEffect(() => {
    if (calendarOpen) return;
    if (isPintagModalSection && firstInputRef.current) {
      // Small delay to ensure the modal is fully rendered
      const timer = setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isPintagModalSection, dateMode, calendarOpen]);

  useEffect(() => {
    // `initialTags` is a prop derived (in the parent) from `cart.find(...)?.pinTag?.tags`.
    // Every time this component's own `onChange` call below succeeds, the parent
    // dispatches `addSession` with a *cloned* session, which reassigns `pinTag.tags`
    // to a brand-new array reference holding the exact same values. That new
    // reference flows back down as a "changed" `initialTags` prop, which would
    // re-run this effect, re-seed the exact same tags, and re-validate them again -
    // forever, once every debounce interval (~300ms), with an identical payload
    // each time. That endless re-validate loop is what produced the continuous
    // Apply-button flicker when opening the Edit Pin Tag drawer for a session that
    // already has a pin tag assigned. Seed once per mount (guarded by
    // `initialSeedDoneRef`) instead of on every reference change.
    if (initialSeedDoneRef.current) return;
    if (!initialColor || sortedPinTags.length === 0) return;

    const lc = initialColor.toLowerCase();

    let matched = sortedPinTags.find(
      (p) => (p.color || "").toLowerCase() === lc
    );

    if (!matched) {
      matched = sortedPinTags.find(
        (p) =>
          (p.alias || "").toLowerCase() === lc ||
          (p.description || "").toLowerCase() === lc
      );
    }

    if (matched) {
      setSelectedColor(matched.alias);
      if (initialTags && initialTags.length) {
        const padded = [
          ...initialTags,
          ...Array(9 - initialTags.length).fill(""),
        ].slice(0, 9);
        // Never fewer boxes than the pintag book rule asks for (this can run
        // after the rule effect and would otherwise override it)
        const session = cart.find((s) => s.sessionId === sessionId);
        const totalItemCount =
          session?.cartItems?.reduce((acc, item) => acc + (item.qty ?? 1), 0) ??
          itemsCount ??
          1;
        const count = Math.min(
          Math.max(
            1,
            padded.filter(Boolean).length,
            Math.ceil(totalItemCount / serviceTagCount)
          ),
          9
        );
        setSelectedTags(padded);
        setTextBoxCount(count);

        onChange?.(
          matched.color,
          padded,
          count,
          matched.id,
          matched.alias,
          matched.day ? matched.day.toUpperCase() : undefined,
          dueDate ? dueDate.format("YYYY-MM-DD") : null,
          pickedSpecial
        );

        initialSeedDoneRef.current = true;
      }
    }
  }, [initialColor, initialTags, sortedPinTags]);

  const dayOfWeek = dueDate ? dueDate.format("dddd") : "";

  useEffect(() => {
    if (dueDate && sortedPinTags.length > 0) {
      const weekday = dueDate.format("dddd").toLowerCase();
      const matched = sortedPinTags.find(
        (tag) =>
          (tag.day ?? "").toLowerCase() === weekday ||
          (tag.description ?? "").toLowerCase() === weekday
      );
      if (matched) {
        setSelectedColor(matched.alias);
        // `safeAssignPinTag` already calls `handleAssignPinTag`, which (for the
        // modal/drawer flow) resolves the full matched-tag metadata itself and
        // invokes `onChange` with it - see the comment in `handleAssignPinTag`.
        // A second, separate `onChange?.(...)` call here used to fire right
        // after with slightly different args (raw `selectedTags` instead of the
        // slice, un-uppercased `day`), which was purely redundant and, worse,
        // doubled the number of onChange invocations fired around drawer-open
        // for a session that already has a pin tag - a contributing factor in
        // the Apply-button flicker bug.
        safeAssignPinTag(matched.alias, selectedTags);
      }
    }
  }, [dueDate, sortedPinTags]);

  const isSameDay = (a?: Dayjs | null, b?: Dayjs | null) =>
    !!a && !!b && a.startOf("day").isSame(b.startOf("day"));

  const lastDatesRef = useRef({ regularDate, specialDate });

  useEffect(() => {
    const last = lastDatesRef.current;
    lastDatesRef.current = { regularDate, specialDate };
    if (!dueDate) return;
    // Don't classify against `regularDate`/`specialDate` until store config
    // has actually resolved - before that they're just guesses, and
    // reclassifying against them here (instead of via the dedicated settle
    // effect above) is exactly how this used to silently downgrade to
    // "custom" once the real config landed after mount.
    if (!isDateConfigReady) return;

    // New rules (store config refreshed on open) or the cut-off passing moved
    // the date of the selected Special/Regular tab: follow it instead of
    // falling back to Custom. A saved date (initialDate) is kept
    if (!initialDate && configSettledRef.current) {
      if (
        dateMode === "special" &&
        dueDate.isSame(last.specialDate) &&
        !specialDate.isSame(last.specialDate)
      ) {
        setDueDate(specialDate);
        return;
      }
      if (
        dateMode === "regular" &&
        dueDate.isSame(last.regularDate) &&
        !regularDate.isSame(last.regularDate)
      ) {
        setDueDate(regularDate);
        return;
      }
    }

    if (isSameDay(dueDate, regularDate)) {
      if (dateMode !== "regular") setDateMode("regular");
      if (calendarOpen) setCalendarOpen(false);
      return;
    }

    if (isSameDay(dueDate, specialDate)) {
      if (dateMode !== "special") setDateMode("special");
      if (calendarOpen) setCalendarOpen(false);
      return;
    }

    if (dateMode !== "custom") {
      setDateMode("custom");
    }
  }, [
    dueDate,
    regularDate,
    specialDate,
    dateMode,
    calendarOpen,
    initialDate,
    isDateConfigReady,
  ]);

  // Memoized so this isn't recreated (and re-run up to ~42x, once per visible
  // calendar day cell) on every render of the open calendar - only recompute when
  // the values it actually depends on change.
  const isDateDisabled = useCallback(
    (d: Date) => {
      const dateToCheck = dayjs(d);
      const isSpecialOrRegular =
        isSameDay(dateToCheck, regularDate) || isSameDay(dateToCheck, specialDate);
      const isNonWorkingDay = !isWorkingDay(d, storeConfig?.body?.workingDays || []);
      const isClosureDate =
        closureDates?.body?.some((cd) =>
          dayjs(cd.closureDate).isSame(dateToCheck, "day")
        ) || false;

      // Calculate earliest valid date based on cutoff time (minimum of Regular and Special dates)
      const regularStart = regularDate.startOf("day");
      const specialStart = specialDate.startOf("day");
      const earliestValidDate = regularStart.isBefore(specialStart)
        ? regularStart
        : specialStart;
      const maxDate = regularStart.isAfter(specialStart)
        ? regularStart
        : specialStart;

      // Disable dates before the earliest valid date (based on cutoff time)
      const isBeforeEarliestValid = dateToCheck.isBefore(earliestValidDate, "day");

      // Disable dates between Regular and Special dates
      const isBetweenRegularAndSpecial =
        dateToCheck.isAfter(earliestValidDate, "day") &&
        dateToCheck.isBefore(maxDate, "day");

      return (
        isSpecialOrRegular ||
        isNonWorkingDay ||
        isClosureDate ||
        isBetweenRegularAndSpecial ||
        isBeforeEarliestValid
      );
    },
    [closureDates, storeConfig, regularDate, specialDate]
  );

  // Typing marks the parent as validating and only its validator cleared that,
  // so when nothing gets sent for validation Apply stayed disabled for good
  const settleValidation = () => onValidationIdle?.();

  const safeAssignPinTag = (color: string | null, tags: string[]) => {
    const targetTagList = tags.slice(0, textBoxCount);
    const isIncompleteTags = targetTagList.some((t) => t.length !== 3);
    const hasEmpty = targetTagList.includes("");
    const badDate = !dueDate || dueDate.isBefore(dayjs().startOf("day"));
    const badColorOrTags = color === null || tags.length === 0;

    if (badColorOrTags || hasEmpty || isIncompleteTags || badDate) {
      if (badDate && !badColorOrTags && !hasEmpty && !isIncompleteTags) {
        snackbar.showSnackbar(
          "Please select a valid future due date",
          "warning"
        );
      }
      setIsCompleted?.(false);
      settleValidation();
      return false;
    }

    const { ok, dupInOther, dupInCurrent, dupIndices } =
      validatePinTagsUniqueness(cart, sessionId, targetTagList);

    if (!ok) {
      console.log("Validation failed", { dupInOther, dupInCurrent, dupIndices });
      const cleared = [...tags];
      dupIndices.forEach((i) => {
        triggerShake(i);
        cleared[i] = "";
      });
      setSelectedTags(cleared);

      if (dupInOther.length) {
        snackbar.showSnackbar(
          `Tag${dupInOther.length > 1 ? "s" : ""} ${dupInOther.join(
            ", "
          )} already used in another session.`,
          "warning"
        );
      }
      if (dupInCurrent.length) {
        snackbar.showSnackbar("Duplicate tag in this session.", "warning");
      }
      setIsCompleted?.(false);
      settleValidation();
      return false;
    }

    handleAssignPinTag(color, tags);
    // For the modal/drawer flow, `handleAssignPinTag` only *kicks off* the
    // debounced, async server-side validation (`onChange`) - it does not
    // resolve synchronously. Marking `isCompleted` true here as well was
    // optimistic: the Apply button would flash enabled immediately, then the
    // debounced validator would flip it back to disabled ~300ms later while it
    // actually validates, then enabled again once that resolves. Let the
    // debounced validator (single source of truth for the async path) own
    // `isCompleted` instead; only report synchronous completion for the
    // non-modal path, where `handleAssignPinTag` dispatches directly with no
    // further async step.
    if (!isPintagModalSection) {
      setIsCompleted?.(true);
    }
    return true;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: isPintagModalSection ? pxToRem(25) : pxToRem(28),
        background: isPintagModalSection ? "none" : Colors.BACKSHADE_GRAY,
        borderRadius: pxToRem(8),
        padding: isPintagModalSection
          ? "0"
          : `${pxToRem(43.305)} ${pxToRem(42.55)}`,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: pxToRem(8),
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: Colors.ACCENT_GRAY,
          borderRadius: pxToRem(4),
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
          borderRadius: pxToRem(4),
        },
      }}
    >
      <Box
        display="flex"
        gap={1}
        width="100%"
        position="relative"
      >
        {[
          {
            labelTop: "Special",
            labelBottom: specialDate.format("ddd DD"),
            value: "special",
          },
          {
            labelTop: "Regular",
            labelBottom: regularDate.format("ddd DD"),
            value: "regular",
          },

          {
            labelTop: "Custom",
            labelBottom: customDate
              ? customDate.format("ddd DD")
              : "Select Date",
            value: "custom",
          },
        ].map((tab) => {
          const isActive = dateMode === tab.value;
          return (
            <Box
              key={tab.value}
              data-label={`tab-${tab.value}`}
              ref={tab.value === "regular" ? regularTabRef : null}
              onClick={() => handleDateModeChange(tab.value as any)}
              sx={{
                flex: 1,
                // Let the three tabs shrink to fit a narrow drawer instead of
                // pushing the content past its edge
                minWidth: 0,
                cursor: "pointer",
                px: "0.4rem",
                py: 1,
                borderRadius: "6px",
                border: isActive
                  ? `2px solid ${Colors.SECONDARY}`
                  : `2px solid ${Colors.ACCENT_GRAY_DISABLED}`,
                backgroundColor: isActive ? Colors.SECONDARY : Colors.WHITE,
                textAlign: "center",
                height: screen == "checkinSummary" ? pxToRem(95) : pxToRem(100),
                whiteSpace: "nowrap",
              }}
            >
              <Typography
                fontWeight="bold"
                fontSize="var(--fs-base)"
                lineHeight={1.2}
                color={isActive ? Colors.WHITE : "#333"}
                marginBottom={window.innerWidth > 1300 ? 1 : 0.5}
              >
                {tab.labelTop}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: isActive
                    ? "rgba(255, 255, 255, 0.2)"
                    : Colors.BACKSHADE_GRAY,
                  paddingX: "0.4rem",
                  paddingY: 0.5,
                  borderRadius: 1,
                  height: pxToRem(30),
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                <Typography
                  fontSize="var(--fs-base)"
                  color={isActive ? Colors.WHITE : "#555"}
                  sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {tab.labelBottom}
                </Typography>
                <CalendarMonthIcon
                  sx={{
                    fontSize: "var(--fs-md)",
                    color: isActive ? Colors.WHITE : "#555",
                    ml: "0.6rem",
                    flexShrink: 0,
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
      {calendarOpen && (
        <Box
          sx={{ width: "100%" }}
        >
          <ShadcnDatePicker
            value={selectedDate}
            onChange={(date: any) => {
              const selected = date;
              const parsed = selected instanceof Date ? dayjs(selected) : null;
              setSelectedDate(selected);
              setCustomDate(parsed);
              setDueDate(parsed);
              setCalendarOpen(false);
              setDateMode("custom");

              // Focus the first Tag Number input after custom date selection
              setTimeout(() => {
                firstInputRef.current?.focus();
              }, 100);
            }}
            open={calendarOpen}
            onOpenChange={setCalendarOpen}
            isDateDisabled={isDateDisabled}
            placeholder="Select custom date"
          />
        </Box>
      )}

      <Box
        sx={{
          // Out of the layout while the Custom calendar is open
          display: calendarOpen ? "none" : "flex",
          flexDirection: mode === "checkin" ? "column" : "row",
          gap: isPintagModalSection ? pxToRem(18.79) : pxToRem(11),
        }}
      >
        <Typography
          sx={{
            width: isPintagModalSection ? pxToRem(174.66) : pxToRem(158),
            fontSize: "1.5rem",
            fontWeight: "bold",
            flexShrink: 0,
            color:
              mode === "checkin" || isPintagModalSection
                ? Colors.TEXT_GRAY_DARK
                : Colors.WHITE,
          }}
        >
          Color Code
        </Typography>
        <Box
          sx={{
            ...(mode === "checkin"
              ? {
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: pxToRem(8),
                width: "100%",
              }
              : {
                display: "flex",
                gap: isPintagModalSection ? pxToRem(14.13) : pxToRem(9.1),
                rowGap: pxToRem(10.61),
                flexWrap: "wrap",
                flexGrow: 1,
              }),
          }}
        >
          {sortedPinTags.map((pinTag) => {
            const isMatch =
              pinTag.description.toLowerCase() === dayOfWeek.toLowerCase();

            return (
              <PinTagButton
                key={pinTag.id}
                color={pinTag.color}
                code={pinTag.alias}
                isSelected={
                  selectedColor === pinTag.alias ||
                  selectedColor === pinTag.color
                }
                disabled={false}
                onClick={() => {
                  if (!isMatch) return;
                  setSelectedColor(pinTag.alias);
                  safeAssignPinTag(pinTag.alias, selectedTags);
                }}
                isPintagModalButton={isPintagModalSection}
                sx={{
                  position: "relative",
                  cursor: isMatch ? "pointer" : "not-allowed",
                  ...(mode === "checkin" && {
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                  }),
                }}
              />
            );
          })}

          {Array.from({
            length: (isPintagModalSection ? 12 : 9) - sortedPinTags.length + 2,
          }).map((_, index) => {
            return (
              <PinTagButton
                key={index}
                color={Colors.ACCENT_GRAY}
                code={"N/A"}
                isSelected={false}
                onClick={() => { }}
                disabled={true}
                isPintagModalButton={isPintagModalSection}
                sx={{
                  ...(mode === "checkin" && {
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                  }),
                }}
              />
            );
          })}
        </Box>
      </Box>
      <Box
        sx={{
          // Out of the layout while the Custom calendar is open
          display: calendarOpen ? "none" : "flex",
          flexDirection: mode === "checkin" ? "column" : "row",
          gap: isPintagModalSection ? pxToRem(18.79) : pxToRem(11),
        }}
      >
        <Typography
          sx={{
            width: isPintagModalSection ? pxToRem(174.66) : pxToRem(158),
            fontSize: "1.5rem",
            fontWeight: "bold",
            flexShrink: 0,
            color: Colors.TEXT_GRAY_DARK,
          }}
        >
          Tag Number(s)
        </Typography>
        <Box
          sx={{
            ...(mode === "checkin"
              ? {
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: pxToRem(8),
                rowGap: pxToRem(11),
                width: "100%",
              }
              : {
                display: "flex",
                gap: pxToRem(8),
                rowGap: pxToRem(11),
                flexWrap: "wrap",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                width: "100%",
              }),
          }}
        >
          {Array.from({ length: 9 }).map((_, index) => {
            const isShaking = shakingIndices.includes(index);
            return (
              <Box
                key={index}
                sx={{
                  width: mode === "checkin"
                    ? "100%"
                    : isPintagModalSection
                      ? pxToRem(139.16)
                      : pxToRem(84.67),
                  height: pxToRem(35.5),
                  "@keyframes shake": {
                    "0%": { transform: "translateX(0)" },
                    "25%": { transform: "translateX(-5px)" },
                    "50%": { transform: "translateX(5px)" },
                    "75%": { transform: "translateX(-5px)" },
                    "100%": { transform: "translateX(0)" },
                  },
                  animation: isShaking ? "shake 0.5s ease-in-out" : "none",
                }}
              >
                <CustomTextBox
                  ref={(el) => {
                    if (index === 0) {
                      (firstInputRef as any).current = el;
                    }
                    tagInputRefs.current.set(index, el);
                  }}
                  inputRef={(input) => {
                    if (input) {
                      inputRefs.current.set(index, input);
                      if (index === 0) {
                        (firstInputRef as any).current = input;
                      }
                    } else {
                      inputRefs.current.delete(index);
                    }
                  }}
                  disabled={!(textBoxCount >= index + 1)}
                  sx={{
                    width: "100%",
                    maxHeight: pxToRem(30),
                    display:
                      !isPintagModalSection && !(textBoxCount >= index + 1)
                        ? "none"
                        : "inline-flex",
                    "& input": {
                      backgroundColor: !(textBoxCount >= index + 1)
                        ? `${Colors.ACCENT_GRAY} !important`
                        : Colors.WHITE,
                      borderRadius: pxToRem(7),
                      maxHeight: pxToRem(30),
                      height: pxToRem(30),
                      padding: `${pxToRem(5.5)} ${mode === "checkin" ? pxToRem(22) : isPintagModalSection ? pxToRem(31.71) : pxToRem(28)}`,
                      fontSize: pxToRem(16),
                      lineHeight: pxToRem(19.2),
                      boxSizing: "border-box",
                      textAlign: "center",
                      caretColor: selectedTags[index]?.length === 3 ? "transparent" : "auto",
                    },
                    "& fieldset": {
                      border: !(textBoxCount >= index + 1)
                        ? `none !important`
                        : `${pxToRem(2)} solid ${isShaking
                          ? Colors.ERROR_RED
                          : selectedTags[index]
                            ? selectedTags[index].length === 3
                              ? Colors.PRIMARY
                              : Colors.ERROR_RED
                            : Colors.ACCENT_GRAY
                        } !important`,
                      borderRadius: pxToRem(7),
                      height: pxToRem(35.5),
                    },
                  }}
                  type="number"
                  value={selectedTags[index] ?? ""}
                  inputProps={{
                    maxLength: 3,
                    max: 999,
                    min: 1,
                  }}
                  size="small"
                  onChange={(e) => {
                    onTypingStart?.();
                    let value = e.target.value.slice(0, 3);

                    const newSelectedTags = [...selectedTags];
                    newSelectedTags[index] = value;
                    setSelectedTags(newSelectedTags);

                    // Auto-focus to next field if current field is completed (3 digits) and next field is available
                    // This happens immediately for better UX, before validation
                    if (value.length === 3 && index < 8) {
                      const nextIndex = index + 1;
                      // Check if next field is available (not disabled)
                      const isNextFieldAvailable = isPintagModalSection ?
                        (nextIndex < 9) : // In modal, allow up to 9 fields
                        (textBoxCount >= nextIndex + 1);

                      if (isNextFieldAvailable) {
                        // Use requestAnimationFrame for better timing with React rendering
                        requestAnimationFrame(() => {
                          setTimeout(() => {
                            const nextInput = inputRefs.current.get(nextIndex);
                            if (nextInput && !nextInput.disabled) {
                              nextInput.focus();
                              // Select all text for easy replacement
                              nextInput.select();
                            }
                          }, 50);
                        });
                      }
                    }

                    // safeAssignPinTag -> handleAssignPinTag is the single call site
                    // that fires the full onChange payload (colorId/alias/day/date)
                    // for the modal section, so the due date is never silently
                    // dropped regardless of whether the selected color has a `.day`
                    // mapping. Do not add a second onChange call here - see the
                    // comment in handleAssignPinTag for why that used to race with
                    // the debounced onChange and drop the due date.
                    safeAssignPinTag(selectedColor, newSelectedTags);
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box
        sx={{
          // Out of the layout while the Custom calendar is open
          display: calendarOpen ? "none" : "flex",
          flexDirection: mode === "checkin" ? "column" : "row",
          gap: isPintagModalSection ? pxToRem(18.79) : pxToRem(11),
          mt: screen == "checkinSummary" ? "" : "",
        }}
      >
        <Typography
          sx={{
            width: isPintagModalSection ? pxToRem(174.66) : pxToRem(158),
            fontSize: "1.5rem",
            fontWeight: "bold",
            flexShrink: 0,
            color: Colors.TEXT_GRAY_DARK,
          }}
        >
          Item Count
        </Typography>
        <CustomTextBox
          type="number"
          value={itemsCount}
          disabled
          sx={{
            width: isPintagModalSection ? pxToRem(91.16) : pxToRem(84.67),
            height: pxToRem(30),
            maxHeight: pxToRem(30),
            borderRadius: pxToRem(7),
            "& input": {
              backgroundColor: `${Colors.ACCENT_GRAY_DISABLED} !important`,
              borderRadius: pxToRem(7),
              boxSizing: "border-box",
              padding: `${pxToRem(8)} ${pxToRem(35)}`,
              textAlign: "center",
              fontSize: pxToRem(16),
              lineHeight: pxToRem(19.2),
              height: pxToRem(30),
              maxHeight: pxToRem(30),
            },
            "& fieldset": {
              border: "none",
              borderRadius: pxToRem(7),
            },
          }}
        />
      </Box>
      <HolidayDatesDialog
        open={holidayDialogOpen}
        onClose={() => setHolidayDialogOpen(false)}
        holidays={skippedHolidays}
        special={{
          date: specialDate,
          isAdminDate: specialIsAdminDate,
          affected: specialHolidays.length > 0,
        }}
        regular={{
          date: regularDate,
          isAdminDate: regularIsAdminDate,
          affected: regularHolidays.length > 0,
        }}
      />
    </Box>
  );
};

export function validatePinTagsUniqueness(
  allSessions: Array<{ sessionId: string; pinTag?: { tags?: string[] } }>,
  currentSessionId: string,
  candidateTags: string[]
): PinTagValidationResult {
  const usedElsewhere = new Set<string>();
  for (const s of allSessions) {
    if (!s || s.sessionId === currentSessionId) continue;
    const ts = s.pinTag?.tags ?? [];
    for (const t of ts) if (t) usedElsewhere.add(String(t));
  }

  const dupInOther: string[] = [];
  const dupIndices: number[] = [];
  candidateTags.forEach((t, idx) => {
    if (t && usedElsewhere.has(t)) {
      dupInOther.push(t);
      dupIndices.push(idx);
    }
  });

  const seen = new Map<string, number>();
  const dupInCurrent: string[] = [];
  candidateTags.forEach((t, idx) => {
    if (!t) return;
    if (seen.has(t)) {
      dupInCurrent.push(t);
      dupIndices.push(idx);
    } else {
      seen.set(t, idx);
    }
  });

  return {
    ok: dupInOther.length === 0 && dupInCurrent.length === 0,
    dupInOther,
    dupInCurrent,
    dupIndices,
  };
}
